import { useEffect, useMemo, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { adminApi } from "../../services/adminApi";
import { Card } from "../../components/common/Card";
import { Spinner } from "../../components/common/Spinner";
import { StatTile } from "../../components/common/StatTile";
import { Badge } from "../../components/common/Badge";
import { DataTable } from "../../components/common/DataTable";
import { GapNotice } from "../../components/common/GapNotice";

const STATUS_TONE = { Open: "info", InProgress: "warning", Resolved: "success", Closed: "neutral" };
// Hours-to-resolve target per priority -- a real, if simplistic, SLA rule
// applied client-side over real created_date/resolved_date data.
const SLA_HOURS = { Critical: 4, High: 24, Medium: 72, Low: 168 };

const TEMPLATES = [
  { title: "Acknowledging a bug report", body: "Thanks for flagging this -- we've reproduced the issue and are working on a fix. We'll update this ticket once it's resolved." },
  { title: "Requesting more info", body: "Could you share a screenshot and the steps that led to this? That'll help us track it down faster." },
  { title: "Feature request received", body: "Thanks for the suggestion! We've logged this as a feature request for the team to prioritize." },
  { title: "Resolved, closing ticket", body: "This should be fixed now -- please let us know if you're still seeing the issue and we'll reopen." },
];

export function SupportTicketsPage() {
  const [tickets, setTickets] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    adminApi.listSupportTickets(statusFilter ? { status: statusFilter } : undefined).then((r) => setTickets(r.tickets)).catch(() => setTickets([]));
  }, [statusFilter]);

  const stats = useMemo(() => {
    if (!tickets) return null;
    const resolved = tickets.filter((t) => t.resolved_date);
    const resolutionHours = resolved.map((t) => (new Date(t.resolved_date) - new Date(t.created_date)) / 3_600_000);
    const avgResolutionHours = resolutionHours.length ? resolutionHours.reduce((a, b) => a + b, 0) / resolutionHours.length : null;
    const withinSla = resolved.filter((t) => {
      const hours = (new Date(t.resolved_date) - new Date(t.created_date)) / 3_600_000;
      return hours <= (SLA_HOURS[t.priority] || 72);
    });
    const slaCompliancePct = resolved.length ? (withinSla.length / resolved.length) * 100 : null;

    const byCategory = {};
    tickets.forEach((t) => { byCategory[t.category] = (byCategory[t.category] || 0) + 1; });

    const byWeek = {};
    tickets.forEach((t) => {
      const d = new Date(t.created_date);
      const week = `${d.getFullYear()}-W${String(Math.ceil((d.getDate() + 6 - d.getDay()) / 7)).padStart(2, "0")}`;
      byWeek[week] = (byWeek[week] || 0) + 1;
    });

    return {
      avgResolutionHours,
      slaCompliancePct,
      byCategory: Object.entries(byCategory).map(([category, count]) => ({ category, count })),
      byWeek: Object.entries(byWeek).map(([week, count]) => ({ week, count })).sort((a, b) => a.week.localeCompare(b.week)),
    };
  }, [tickets]);

  async function updateStatus(ticketId, status) {
    await adminApi.updateSupportTicket(ticketId, status);
    setTickets((prev) => prev.map((t) => (t.ticket_id === ticketId ? { ...t, status } : t)));
  }

  if (!tickets || !stats) return <Spinner />;

  return (
    <div>
      <h1>Support Ticket Management</h1>

      <div className="section-spacing" style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
        <StatTile label="Total Tickets" value={tickets.length} />
        <StatTile label="Avg Resolution Time" value={stats.avgResolutionHours != null ? `${stats.avgResolutionHours.toFixed(1)}h` : "--"} />
        <StatTile label="SLA Compliance" value={stats.slaCompliancePct != null ? `${stats.slaCompliancePct.toFixed(1)}%` : "--"} sublabel="vs. priority-based target" />
      </div>

      <div className="section-spacing" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)" }}>
        <Card title="Tickets by Category">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={stats.byCategory}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="category" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#3b82c4" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Ticket Volume by Week">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={stats.byWeek}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="week" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#f2a900" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card
        title="All Tickets"
        className="section-spacing"
        actions={
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="touch-target">
            <option value="">All statuses</option>
            {["Open", "InProgress", "Resolved", "Closed"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        }
      >
        <DataTable
          rows={tickets}
          rowKey="ticket_id"
          emptyTitle="No support tickets"
          columns={[
            { key: "subject", header: "Subject" },
            { key: "category", header: "Category" },
            { key: "priority", header: "Priority" },
            { key: "status", header: "Status", render: (r) => <Badge tone={STATUS_TONE[r.status]}>{r.status}</Badge> },
            { key: "created_date", header: "Created", render: (r) => new Date(r.created_date).toLocaleDateString() },
            {
              key: "actions", header: "", render: (r) => (
                <select value={r.status} onChange={(e) => updateStatus(r.ticket_id, e.target.value)} className="touch-target">
                  {["Open", "InProgress", "Resolved", "Closed"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              ),
            },
          ]}
        />
      </Card>

      <Card title="Response Templates" className="section-spacing">
        {TEMPLATES.map((t) => (
          <div key={t.title} style={{ padding: "var(--space-2) 0", borderBottom: "1px solid var(--color-border)" }}>
            <strong>{t.title}</strong>
            <p style={{ color: "var(--color-text-muted)", margin: "4px 0 0" }}>{t.body}</p>
          </div>
        ))}
      </Card>

      <Card title="Zendesk/Intercom Integration & First-Response Time">
        <GapNotice>
          app.SupportTickets.zendesk_id (V015) already reserves a column for an external ticketing ID, but no sync job
          actually calls the Zendesk/Intercom API to keep it current -- tickets created here are standalone.
          "Response Time" above is really resolution time (created_date → resolved_date, both real columns); a true
          first-response metric needs a separate timestamp for the first admin reply, which isn't tracked.
        </GapNotice>
      </Card>
    </div>
  );
}
